import { useEffect, useState } from 'react'

import TextArea from '../inputs/TextArea'
import TextField from '../inputs/TextField'

export default function SplitQuestion({
  name,
  value,
  onChange,
  disabled,
  errors,
  // subName,
  wordCount,
}: any) {
  const [initiative, setInitiative] = useState('')
  const [timeframe, setTimeframe] = useState('')
  const [metric, setMetric] = useState('')
  const [goal, setGoal] = useState('')
  const [currentSlide, setCurrentSlide] = useState(0)
  const [fieldErrors, setFieldErrors] = useState<any>()

  const staticLabels = {
    initiative: 'We will',
    timeframe: 'Over the next',
    metric: 'with',
    goal: 'To help us achieve our ambition/goal of',
  }
  const handleInputChange = (field: string, newValue: string) => {
    const totalText = [
      `${staticLabels.initiative} ${field === 'initiative' ? newValue : initiative}`,
      `${staticLabels.timeframe} ${field === 'timeframe' ? newValue : timeframe}`,
      `${staticLabels.metric} ${field === 'metric' ? newValue : metric}`,
      `${staticLabels.goal} ${field === 'goal' ? newValue : goal}`,
    ]
      .join(' ')
      .trim()

    const words = totalText.split(/\s+/)

    // Restrict input if word count exceeds the limit
    if (words.length > wordCount) {
      setFieldErrors(`Total word count cannot exceed ${wordCount} words`)
      return
    } else {
      setFieldErrors('')
    }

    // Update the appropriate field state immediately
    switch (field) {
      case 'initiative':
        setInitiative(newValue)
        break
      case 'timeframe':
        setTimeframe(newValue)
        break
      case 'metric':
        setMetric(newValue)
        break
      case 'goal':
        setGoal(newValue)
        break
    }

    const newInitiative = field === 'initiative' ? newValue : initiative
    const newTimeframe = field === 'timeframe' ? newValue : timeframe
    const newMetric = field === 'metric' ? newValue : metric
    const newGoal = field === 'goal' ? newValue : goal

    // Update parent with new combined value
    const combinedValue = combineValues(
      newInitiative,
      newTimeframe,
      newMetric,
      newGoal
    )
    onChange(combinedValue)
  }

  const combineValues = (
    init: string,
    time: string,
    met: string,
    gl: string
  ) => {
    const hasValues = init.trim() || time.trim() || met.trim() || gl.trim()
    return hasValues ? `*#&#*${init}*#&#*${time}*#&#*${met}*#&#*${gl}` : ''
  }

  useEffect(() => {
    if (value) {
      // Only parse if the value is different from what we would generate
      const currentCombined = combineValues(initiative, timeframe, metric, goal)
      if (value !== currentCombined) {
        parseCombinedValue(value)
      }
    } else {
      setInitiative('')
      setTimeframe('')
      setMetric('')
      setGoal('')
    }
  }, [value])

  const parseCombinedValue = (combinedValue: string) => {
    const parts = combinedValue.split('*#&#*').map((part) => part.trim())
    if (parts.length >= 5) {
      setInitiative(parts[1] || '')
      setTimeframe(parts[2] || '')
      setMetric(parts[3] || '')
      setGoal(parts[4] || '')
    }
  }
  const getErrors = (err: any) => {
    let errMsg = ''
    if (err && err?.message) {
      errMsg = err?.message
    }
    return errMsg
  }
  const exampleSlides = [
    [
      {
        title: 'Example 1',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [develop and implement a system to track and monitor employee
              data]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">[year]</span>{' '}
            <span className="font-medium text-danger">
              [metric not applicable]
            </span>{' '}
            to help us achieve our goal of{' '}
            <span className="text-primary font-medium">
              [generating reports to identify baselines and trends for future
              strategic ED&I related decisions]
            </span>
          </>
        ),
      },
      {
        title: 'Example 2',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [conduct a data and engagement research project]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">
              [year] [engaging 35 women]
            </span>{' '}
            to help us achieve our goal of{' '}
            <span className="text-primary font-medium">
              [understanding why we have so few women applying for operational
              roles]
            </span>
          </>
        ),
      },
    ],
    [
      {
        title: 'Example 3',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [develop a leadership mentoring programme]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">
              {' '}
              [two years] [with 25 mentees]{' '}
            </span>
            to help us achieve our ambition/goal of{' '}
            <span className="text-primary font-medium">
              [gender balance at senior level by 2030]
            </span>
          </>
        ),
      },
      {
        title: 'Example 4',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [Develop a maternity return-to-work programme]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">
              {' '}
              [2 years] [with 100% uptake]{' '}
            </span>
            to help us achieve our goal of {''}
            <span className="text-primary font-medium">
              [80% retention within 2 years after maternity]
            </span>
          </>
        ),
      },
    ],
    [
      {
        title: 'Example 5',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [Develop an Equality, Diversity and Inclusion strategy]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">[year] </span>
            <span className="font-medium text-danger">
              [metric not applicable]
            </span>{' '}
            to help us achieve our goal of{' '}
            <span className="text-primary font-medium">
              [enhancing stakeholder trust and widening our talent pool]
            </span>
          </>
        ),
      },
      {
        title: 'Example 6',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [develop a Disability Action Plan]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">[year]</span>
            <span className="font-medium text-danger">
              [with metric/number]
            </span>{' '}
            to help us achieve our goal of {''}
            <span className="text-primary font-medium">
              [understanding the barriers and opportunities in improving
              workplace accessibility and disability representation]
            </span>
          </>
        ),
      },
    ],
    [
      {
        title: 'Example 7',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [expand our STEM education outreach]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">
              [2 years] [with 2 primary & 2 post primary girls’ schools]{' '}
            </span>
            to help us achieve our goal of{' '}
            <span className="text-primary font-medium">
              [help encourage more girls to pursue careers in STEM]
            </span>
          </>
        ),
      },
      {
        title: 'Example 8',
        content: (
          <>
            We will{' '}
            <span className="text-primary font-medium">
              [implement a focussed outreach programme]
            </span>{' '}
            over the next{' '}
            <span className="text-primary font-medium">
              [3 years] [to attract 10 young people from socially disadvantaged
              communities]{''}
            </span>
            {''} to help us achieve our goal of {''}
            <span className="text-primary font-medium">
              [increase representation from disadvantaged backgrounds to 10% by
              2030]
            </span>
          </>
        ),
      },
    ],
  ]

  const isFirst = currentSlide === 0
  const isLast = currentSlide === exampleSlides.length - 1

  const handleNext = () => {
    if (!isLast) {
      setCurrentSlide((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    if (!isFirst) {
      setCurrentSlide((prev) => prev - 1)
    }
  }
  return (
    <div>
      <div className="p-4 text-primary rounded-lg flex items-start gap-2 mb-4 bg-[#f6f4f9]">
        <div className="flex flex-col gap-1.5 min-h-[200px]">
          {exampleSlides[currentSlide].map((example, idx) => (
            <div key={idx}>
              <p className="text-primary text-xxs leading-4 font-semibold mb-1">
                {example.title}
              </p>
              <p className="text-black text-xxs leading-5 font-semibold italic">
                {example.content}
              </p>
            </div>
          ))}

          <div className="flex justify-between mt-auto">
            {isFirst ? (
              <span />
            ) : (
              <span
                className="text-grey-medium cursor-pointer font-medium  select-none text-common"
                onClick={handleBack}
              >
                Back
              </span>
            )}

            {!isLast && (
              <span
                className="text-linkBlue cursor-pointer font-medium select-none  text-common"
                onClick={handleNext}
              >
                See more Examples
              </span>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="split-question-container">
          <label className=" split-question-label text-xs text-blackAlt font-normal leading-4 ">
            We will <span className="text-error"> *</span>
          </label>
          <TextArea
            id="initiative"
            name="initiative"
            value={initiative}
            placeholder="Mention your Initiative/project"
            onChange={(e) => handleInputChange('initiative', e.target.value)}
            disabled={disabled}
            // errorFlag={!initiative ? true : fieldErrors}
            className={`h-[72px] !min-h-[72px] ${!initiative ? 'textfield-error' : fieldErrors ? 'textfield-error' : ''}`}
          />
        </div>

        <div className="split-question-container">
          <label className="split-question-label text-xs text-blackAlt font-normal leading-4 ">
            Over the next <span className="text-error"> *</span>
          </label>
          <div className="max-w-[180px] w-full">
            <TextField
              id="timeframe"
              type="text"
              name="timeframe"
              value={timeframe}
              placeholder="Include your timeframe"
              onChange={(e) => handleInputChange('timeframe', e.target.value)}
              disabled={disabled}
              errorFlag={!timeframe ? true : fieldErrors}
            />
          </div>
          <label className=" split-question-label text-xs text-blackAlt font-normal leading-4 ml-2">
            with
          </label>
          <TextField
            id="metric"
            type="text"
            name="metric"
            value={metric}
            placeholder="Include metric/number if applicable*"
            onChange={(e) => handleInputChange('metric', e.target.value)}
            disabled={disabled}
            errorFlag={fieldErrors}
            // className="ml-3"
          />
        </div>

        <div className="split-question-container items-start">
          <label className="split-question-label text-xs text-blackAlt font-normal mb-2 leading-4 ">
            to help us achieve our <br />
            ambition/goal of<span className="text-error"> *</span>
          </label>

          <TextArea
            id="goal"
            name="goal"
            value={goal}
            placeholder="Include your short- or long-term goal"
            onChange={(e) => handleInputChange('goal', e.target.value)}
            disabled={disabled}
            // errorFlag={!goal || fieldErrors}
            className={`h-[72px] !min-h-[72px] ${!goal ? 'textfield-error' : fieldErrors ? 'textfield-error' : ''}`}
          />
        </div>
      </div>
      {fieldErrors ? (
        <div className="text-error text-error-label mt-[1px]">
          {fieldErrors}
        </div>
      ) : (
        errors?.[name] && (
          <div className="text-error text-error-label mt-[1px]">
            {getErrors(errors?.[name])}
          </div>
        )
      )}
    </div>
  )
}
